import * as THREE from "three";
import { HorizontalBlurShader } from "three/examples/jsm/shaders/HorizontalBlurShader.js";
import { VerticalBlurShader } from "three/examples/jsm/shaders/VerticalBlurShader.js";

/**
 * Soft contact shadow tunables. Inlined here (this branch has no central
 * constants module) — see createContactShadow below for what each one does.
 */
const SHADOW = {
  /** Render-target resolution for the baked shadow. */
  MAP_SIZE: 1024,
  /** Final shadow plane opacity (overall darkness, 0..1). */
  OPACITY: 0.5,
  /** Depth→darkness multiplier; higher = denser shadow near the contact. */
  DARKNESS: 1.3,
  /**
   * Exponent on the depth→darkness falloff (`(1 - depth) ^ FALLOFF`). 1 = linear.
   * Higher values keep the dark contact under the feet (depth ≈ 0) but make the
   * higher-up seat fade away much faster, so it reads as a soft light pool
   * rather than a solid patch.
   */
  FALLOFF: 2.4,
  /** Gaussian blur amount (penumbra softness). */
  BLUR: 3.2,
  /** Shadow plane size relative to the model footprint. */
  AREA_SCALE: 2.2,
  /**
   * Capture height above the floor, relative to the model height. Kept low so
   * the depth gradient concentrates near the floor — the leg contacts read as
   * the darkest points and the bulky seat above fades to a soft pool, instead
   * of the seat projecting one solid dark blob.
   */
  HEIGHT_SCALE: 0.7,
} as const;

export interface ContactShadow {
  /** Group to add to the scene (holds the shadow plane). */
  group: THREE.Group;
  /** (Re)bake the shadow from the current model state. */
  bake: () => void;
  /** Free all GPU resources. */
  dispose: () => void;
}

/**
 * Soft, baked contact shadow — the classic three.js render-to-texture technique
 * (cf. the webgl_shadow_contact example), which is what gives the make3db /
 * product-shot look: a wide, evenly-soft shadow that darkens under the contact
 * points and dissolves outward, with no hard shadow-map edges.
 *
 * How it works: an orthographic camera under the model renders the geometry as
 * depth into a render target (closer to the floor → darker), the result is blur-
 * red in two passes, and the blurred texture is shown on a flat ground plane.
 * The shadow is independent of the orbit camera, so we bake it once after the
 * model loads instead of every frame.
 */
export function createContactShadow({
  renderer,
  scene,
  floorY,
  center,
  size,
}: {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  floorY: number;
  center: THREE.Vector3;
  size: THREE.Vector3;
}): ContactShadow {
  const area = Math.max(size.x, size.z) * SHADOW.AREA_SCALE;
  const cameraHeight = Math.max(size.y * SHADOW.HEIGHT_SCALE, 0.001);
  const res = SHADOW.MAP_SIZE;

  const group = new THREE.Group();
  group.position.set(center.x, floorY, center.z);

  // Render targets: one to capture depth, one to ping-pong the blur.
  const renderTarget = new THREE.WebGLRenderTarget(res, res);
  renderTarget.texture.generateMipmaps = false;
  const renderTargetBlur = new THREE.WebGLRenderTarget(res, res);
  renderTargetBlur.texture.generateMipmaps = false;

  // Visible plane that shows the baked shadow texture.
  const plane = new THREE.Mesh(
    new THREE.PlaneGeometry(area, area).rotateX(Math.PI / 2),
    new THREE.MeshBasicMaterial({
      map: renderTarget.texture,
      opacity: SHADOW.OPACITY,
      transparent: true,
      depthWrite: false,
    }),
  );
  // The render target is captured upside-down; flip the plane to compensate.
  plane.scale.y = -1;
  plane.renderOrder = -1;
  group.add(plane);

  // Camera under the model looking up, capturing the footprint.
  const shadowCamera = new THREE.OrthographicCamera(
    -area / 2,
    area / 2,
    area / 2,
    -area / 2,
    0,
    cameraHeight,
  );
  shadowCamera.rotation.x = Math.PI / 2; // look up
  group.add(shadowCamera);

  // Off-screen plane used only as the blur pass quad. It MUST share the same
  // horizontal (rotateX) orientation as the visible plane: the shadow camera
  // looks straight up, so a non-rotated (vertical) quad would be edge-on to it
  // and the blur passes would sample/write nothing — wiping out the shadow.
  const blurPlane = new THREE.Mesh(
    new THREE.PlaneGeometry(area, area).rotateX(Math.PI / 2),
  );
  blurPlane.visible = false;
  group.add(blurPlane);

  // Depth material: black, with alpha = (1 - normalisedDepth) * darkness, so
  // geometry near the floor writes a denser shadow than geometry high up — this
  // is what concentrates the shadow under the feet/legs and fades it under the
  // seat. `darkness` is injected as a GLSL literal (a custom uniform on the
  // built-in depth material isn't reliably uploaded across three versions).
  const depthMaterial = new THREE.MeshDepthMaterial();
  const darknessLiteral = SHADOW.DARKNESS.toFixed(4);
  const falloffLiteral = SHADOW.FALLOFF.toFixed(4);
  depthMaterial.onBeforeCompile = (shader) => {
    shader.fragmentShader = shader.fragmentShader.replace(
      "gl_FragColor = vec4( vec3( 1.0 - fragCoordZ ), opacity );",
      `gl_FragColor = vec4( vec3( 0.0 ), pow( 1.0 - fragCoordZ, ${falloffLiteral} ) * ${darknessLiteral} );`,
    );
  };
  // Depth-test ON so the fragment NEAREST the floor wins per pixel (the camera
  // looks straight up, so nearest = the feet). With it off, the material is
  // opaque and last-triangle-wins: the overhanging seat overwrites the front
  // leg contacts (front feet vanish, seat prints a dark blob). Nearest-wins
  // keeps all four contacts and lets the seat only fill where no leg is below.
  depthMaterial.depthTest = true;
  depthMaterial.depthWrite = true;

  const hBlur = new THREE.ShaderMaterial(HorizontalBlurShader);
  hBlur.depthTest = false;
  const vBlur = new THREE.ShaderMaterial(VerticalBlurShader);
  vBlur.depthTest = false;

  function blurShadow(amount: number) {
    blurPlane.visible = true;

    blurPlane.material = hBlur;
    hBlur.uniforms.tDiffuse.value = renderTarget.texture;
    hBlur.uniforms.h.value = (amount * 1) / 256;
    renderer.setRenderTarget(renderTargetBlur);
    renderer.render(blurPlane, shadowCamera);

    blurPlane.material = vBlur;
    vBlur.uniforms.tDiffuse.value = renderTargetBlur.texture;
    vBlur.uniforms.v.value = (amount * 1) / 256;
    renderer.setRenderTarget(renderTarget);
    renderer.render(blurPlane, shadowCamera);

    blurPlane.visible = false;
  }

  function bake() {
    const prevBackground = scene.background;
    const prevClearAlpha = renderer.getClearAlpha();

    // Hide our own plane so it isn't captured as a depth caster.
    plane.visible = false;
    scene.background = null;
    scene.overrideMaterial = depthMaterial;
    renderer.setClearAlpha(0);

    renderer.setRenderTarget(renderTarget);
    renderer.clear();
    renderer.render(scene, shadowCamera);

    scene.overrideMaterial = null;

    // Two blur passes (the second, lighter, smooths the remaining grain).
    blurShadow(SHADOW.BLUR);
    blurShadow(SHADOW.BLUR * 0.4);

    renderer.setRenderTarget(null);
    renderer.setClearAlpha(prevClearAlpha);
    scene.background = prevBackground;
    plane.visible = true;
  }

  function dispose() {
    renderTarget.dispose();
    renderTargetBlur.dispose();
    plane.geometry.dispose();
    (plane.material as THREE.Material).dispose();
    blurPlane.geometry.dispose();
    depthMaterial.dispose();
    hBlur.dispose();
    vBlur.dispose();
  }

  return { group, bake, dispose };
}
